/**
 * Tagging for keyboard events the tour dispatches ITSELF.
 *
 * Some tour steps drive interactions that only the keyboard reaches — e.g. the
 * Trades "Row actions" frame clears the selected row on leave by simulating
 * Escape, which `useRowSelection` listens for on `document`. That synthetic
 * Escape bubbles all the way up to `window`, where TourProvider has its OWN
 * keydown listener that treats Escape as "user wants out" and calls `skip()`.
 * The result: leaving that frame killed the entire tour (LR-446).
 *
 * To break the collision, the tour tags every event it dispatches and its
 * window-level handler ignores tagged events — only genuine user key presses
 * advance/rewind/skip the tour. The flag lives in this standalone module (not
 * `tour-steps` / `TourProvider`) so it can be shared without import cycles and
 * imported real by tests that mock `tour-steps`.
 */
const TOUR_SYNTHETIC_EVENT_FLAG = '__lrTourSynthetic';

/** Build a bubbling `keydown` event tagged so TourProvider's handler ignores it. */
export const makeTourSyntheticKeyEvent = (key: string): KeyboardEvent => {
  const ev = new KeyboardEvent('keydown', { key, bubbles: true });
  (ev as KeyboardEvent & Record<string, unknown>)[TOUR_SYNTHETIC_EVENT_FLAG] = true;
  return ev;
};

/** True when the event was dispatched by the tour itself (see module docstring). */
export const isTourSyntheticEvent = (e: Event): boolean =>
  (e as Event & Record<string, unknown>)[TOUR_SYNTHETIC_EVENT_FLAG] === true;
